import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';

import { goBackToForm, goToGivenSlotForm, goToMultipleCalendar } from 'FRONTEND/reducers/calendarSlice';
import { createPatient, getPatients, updatePatient } from 'FRONTEND/utils/backendApi/patient';
import { getDeserializedState } from 'FRONTEND/utils/serialization';

const REDUCER_NAME = 'patient';
const patientAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.lastname?.localeCompare(b.lastname),
});
const storedState = getDeserializedState()?.[REDUCER_NAME];

const initialState = {
  ...patientAdapter.getInitialState(),
  matchingIdList: [],
  error: null,
  isExpectingCreation: false,
  isExpectingEdition: false,
  ...storedState,
};
export const listOnePatient = createAsyncThunk(`${REDUCER_NAME}/listOne`, async({ name }) => {
  const patientList = await getPatients({ name, limit: 1 });
  return { patient: patientList[0] };
});

export const searchPatients = createAsyncThunk(`${REDUCER_NAME}/searchPatients`, async({ name }) => {
  return { patient: await getPatients({ name }) };
});

export const createOnePatient = createAsyncThunk(`${REDUCER_NAME}/createPatient`, async({ patientFormData }) => {
  return { patient: await createPatient({ patientFormData }) };
});

export const updateOnePatient = createAsyncThunk(`${REDUCER_NAME}/updatePatient`, async({ patientFormData, logicalId }) => {
  return { patient: await updatePatient({ patientFormData, logicalId }) };
});
const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    clearSearch(state) {
      state.matchingIdList = [];
    },
    expectCreation(state) {
      state.isExpectingCreation = true;
    },
    expectEdition(state) {
      state.isExpectingEdition = true;
    },
    abortCreation(state) {
      state.isExpectingCreation = false;
    },
    abortEdition(state) {
      state.isExpectingEdition = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(goToGivenSlotForm, (state) => {
      state.isExpectingCreation = false;
      state.isExpectingEdition = false;
    });
    builder.addCase(goToMultipleCalendar, (state) => {
      state.isExpectingCreation = false;
      state.isExpectingEdition = false;
    });
    builder.addCase(goBackToForm, (state) => {
      state.isExpectingCreation = false;
      state.isExpectingEdition = false;
    });
    builder.addCase(listOnePatient.fulfilled, (state, { payload }) => {
      patientAdapter.upsertOne(state, payload.patient);
    });
    builder.addCase(searchPatients.fulfilled, (state, { payload }) => {
      patientAdapter.setAll(state, payload.patient);
      state.matchingIdList = payload.patient.map((patient) => patient.id);
    });
    builder.addCase(createOnePatient.fulfilled, (state, { payload }) => {
      patientAdapter.addOne(state, payload.patient);
      if (state.isExpectingCreation) {
        state.isExpectingCreation = false;
      }
    });
    builder.addCase(updateOnePatient.fulfilled, (state, { payload }) => {
      patientAdapter.updateOne(state, { id: payload.patient.id, changes: { ...payload.patient } });
      if (state.isExpectingEdition) {
        state.isExpectingEdition = false;
      }
    });
    builder.addMatcher((action) => [updateOnePatient.rejected.toString(), createOnePatient.rejected.toString(), searchPatients.rejected.toString(), listOnePatient.rejected.toString()].includes(action.type), (state, { error }) => {
      state.error = error;
    });
    builder.addDefaultCase(() => {});
  },
});

export const selectAllMatchingPatients = (state) => state.patient.matchingIdList.map((id) => state.patient.entities[id]);

export const selectAllMatchingPatientsByTenant = createSelector(
  selectAllMatchingPatients,
  (_, tenantId) => tenantId,
  (patientsList, tenantId) => {
    if (!tenantId) {
      return patientsList;
    }
    return patientsList.filter((patient) => patient.tenantId === tenantId);
  },
);

export const {
  selectById: selectPatientById,
  selectAll: selectAllPatients,
} = patientAdapter.getSelectors((state) => state.patient);
export const {
  searchRequest,
  searchSuccess,
  searchError,
  clearSearch,
  createRequest,
  createSuccess,
  createError,
  updateRequest,
  updateSuccess,
  updateError,
  expectCreation,
  expectEdition,
  abortCreation,
  abortEdition,
} = patientSlice.actions;
export default patientSlice.reducer;
